import {
  type ReactNode,
  createContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAccount } from 'wagmi';

interface SignatureContextType {
  signature: string | null;
  setSignature: (signature: string | null) => void;
  isSignatureLoading: boolean;
  aiAddress: string | null;
  setAiAddress: (aiAddress: string | null) => void;
}

export const SignatureContext = createContext<SignatureContextType>({
  signature: null,
  setSignature: () => {},
  isSignatureLoading: true,
  aiAddress: null,
  setAiAddress: () => {},
});

export const SignatureProvider = ({ children }: { children: ReactNode }) => {
  const { address } = useAccount();
  const [signature, setSignature] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const storedSignature = localStorage.getItem('userSignature');
      console.log('Loaded signature from localStorage:', storedSignature);
      return storedSignature;
    }
    return null;
  });

  const [aiAddress, setAiAddress] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const storedAiAddress = localStorage.getItem('aiAddress');
      console.log('Loaded AI Address from localStorage:', storedAiAddress);
      return storedAiAddress;
    }
    return null;
  });

  const [isSignatureLoading, setIsSignatureLoading] = useState<boolean>(true);
  const previousAddress = useRef<string | null>(null);

  // Clear signature when wallet changes
  useEffect(() => {
    if (previousAddress.current && address !== previousAddress.current) {
      console.log('Wallet changed, clearing signature');
      setSignature(null);
      setAiAddress(null);
      localStorage.removeItem('userSignature');
      localStorage.removeItem('aiAddress');
    }
    previousAddress.current = address as string;
  }, [address]);

  // useRef to keep track of initial load to prevent useEffect from running on mount
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      setIsSignatureLoading(false);
      isFirstRun.current = false;
      return;
    }

    if (signature) {
      const storedSignature = localStorage.getItem('userSignature');
      if (storedSignature !== signature) {
        localStorage.setItem('userSignature', signature);
        console.log('Saved signature to localStorage:', signature);
      }
    } else {
      localStorage.removeItem('userSignature');
      console.log('Removed signature from localStorage');
    }
  }, [signature]);

  useEffect(() => {
    if (isFirstRun.current) {
      // Avoid running on the first render
      isFirstRun.current = false;
      return;
    }

    if (aiAddress) {
      const storedAiAddress = localStorage.getItem('aiAddress');
      if (storedAiAddress !== aiAddress) {
        localStorage.setItem('aiAddress', aiAddress);
        console.log('Saved AI Address to localStorage:', aiAddress);
      }
    } else {
      localStorage.removeItem('aiAddress');
      console.log('Removed AI Address from localStorage');
    }
  }, [aiAddress]);

  return (
    <SignatureContext.Provider
      value={{
        signature,
        setSignature,
        isSignatureLoading,
        aiAddress,
        setAiAddress,
      }}
    >
      {children}
    </SignatureContext.Provider>
  );
};
